import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { useSeo } from "@/lib/seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { PortalFooter } from "@/components/PortalFooter";

const pwRules = (pw) => ({
  length: pw.length >= 12 && pw.length <= 128,
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
  number: /[0-9]/.test(pw),
  symbol: /[^A-Za-z0-9]/.test(pw),
});
const pwValid = (pw) => Object.values(pwRules(pw)).every(Boolean);

const RULE_LABELS = {
  length: "12–128 characters",
  upper: "An uppercase letter",
  lower: "A lowercase letter",
  number: "A number",
  symbol: "A symbol",
};

export default function PortalLogin() {
  useSeo({ title: "Merchant Login", description: "Manage your MONOE business plan and billing.", path: "/business/login" });
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | otp | reset-email | reset-confirm
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loginNotice, setLoginNotice] = useState("");

  const go = (m) => { setMode(m); setError(""); setNotice(""); };

  const submitPassword = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await portalApi.post("/auth/login", { email, password });
      if (data.authenticated) return navigate("/business/billing");
      if (data.requires_otp) { setChallengeId(data.challenge_id); go("otp"); return; }
      if (data.requires_verification) { setError("Please verify your email in the MONOE app before signing in on the web."); return; }
    } catch (err) {
      setError(err?.response?.data?.detail || "Sign in failed. Check your email and password.");
    } finally { setBusy(false); }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const { data } = await portalApi.post("/auth/otp", { challenge_id: challengeId, code });
      if (data.authenticated) return navigate("/business/billing");
    } catch (err) {
      setError(err?.response?.data?.detail || "Invalid or expired code.");
    } finally { setBusy(false); }
  };

  const requestReset = async (e) => {
    if (e) e.preventDefault();
    if (!email.includes("@")) { setError("Enter your account email."); return; }
    setError(""); setBusy(true);
    try {
      const { data } = await portalApi.post("/auth/password-reset/request", { email });
      setNotice(data?.message || "If that email exists, a reset code has been sent.");
      setMode("reset-confirm");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not request a reset. Please try again.");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setError(""); setResending(true);
    try {
      const { data } = await portalApi.post("/auth/password-reset/request", { email });
      setNotice(data?.message || "A new code has been sent if that email exists.");
      setCode("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not resend. Please try again.");
    } finally { setResending(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^\d{6}$/.test(code)) { setError("Enter the 6-digit code from your email."); return; }
    if (!pwValid(newPassword)) { setError("Your new password doesn't meet the requirements below."); return; }
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    setBusy(true);
    try {
      await portalApi.post("/auth/password-reset/confirm", { email, code, new_password: newPassword });
      // Clear any lingering portal session; reset password/code fields.
      try { await portalApi.post("/auth/logout"); } catch { /* best effort */ }
      setPassword(""); setCode(""); setNewPassword(""); setConfirmPassword("");
      setLoginNotice("Password changed. Please log in again.");
      go("login");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not reset your password. Please try again.");
    } finally { setBusy(false); }
  };

  const rules = pwRules(newPassword);

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center px-4 py-16">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex justify-center"><Logo /></div>
          <div className="rounded-xl border border-border bg-card p-6 sm:p-8">
            {/* ---------------- LOGIN ---------------- */}
            {mode === "login" && (
              <>
                <h1 className="font-display text-xl font-bold tracking-tight">Manage your business plan</h1>
                <p className="mt-1 text-sm text-muted-foreground">Sign in with your MONOE account.</p>
                {loginNotice && (
                  <div className="mt-4 flex items-center gap-2 rounded-md border border-emerald-600/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300" data-testid="portal-login-notice">
                    <CheckCircle2 className="h-4 w-4" />{loginNotice}
                  </div>
                )}
                <form onSubmit={submitPassword} className="mt-6 space-y-4" data-testid="portal-login-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-login-email" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password">Password</Label>
                    <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="portal-login-password" required />
                  </div>
                  {error && <p className="text-sm text-red-600 dark:text-red-400" data-testid="portal-login-error">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="portal-login-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-50">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Signing in…" : "Continue"}
                  </button>
                  <button type="button" onClick={() => { setLoginNotice(""); go("reset-email"); }} data-testid="portal-forgot-link"
                    className="block w-full text-center text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline">
                    Forgot password?
                  </button>
                </form>
              </>
            )}

            {/* ---------------- OTP ---------------- */}
            {mode === "otp" && (
              <>
                <h1 className="font-display text-xl font-bold tracking-tight">Enter your login code</h1>
                <p className="mt-1 text-sm text-muted-foreground">We sent a verification code to your email.</p>
                <form onSubmit={submitOtp} className="mt-6 space-y-4" data-testid="portal-otp-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="otp">Verification code</Label>
                    <Input id="otp" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} data-testid="portal-otp-code" required />
                  </div>
                  {error && <p className="text-sm text-red-600 dark:text-red-400" data-testid="portal-otp-error">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="portal-otp-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-50">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Verifying…" : "Verify"}
                  </button>
                  <button type="button" onClick={() => { setCode(""); go("login"); }} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Back
                  </button>
                </form>
              </>
            )}

            {/* ---------------- RESET: EMAIL ---------------- */}
            {mode === "reset-email" && (
              <>
                <h1 className="font-display text-xl font-bold tracking-tight">Reset your password</h1>
                <p className="mt-1 text-sm text-muted-foreground">Enter your MONOE account email and we'll send you a 6-digit reset code.</p>
                <form onSubmit={requestReset} className="mt-6 space-y-4" data-testid="portal-reset-email-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-email">Email</Label>
                    <Input id="reset-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="portal-reset-email" required />
                  </div>
                  {error && <p className="text-sm text-red-600 dark:text-red-400" data-testid="portal-reset-email-error">{error}</p>}
                  <button type="submit" disabled={busy} data-testid="portal-reset-send"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-50">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Sending…" : "Send reset code"}
                  </button>
                  <button type="button" onClick={() => go("login")} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                    <ArrowLeft className="h-4 w-4" /> Back to sign in
                  </button>
                </form>
              </>
            )}

            {/* ---------------- RESET: CONFIRM ---------------- */}
            {mode === "reset-confirm" && (
              <>
                <h1 className="font-display text-xl font-bold tracking-tight">Enter code & new password</h1>
                {notice && (
                  <div className="mt-3 rounded-md border border-border bg-secondary px-3 py-2 text-sm text-muted-foreground" data-testid="portal-reset-notice">{notice}</div>
                )}
                <form onSubmit={confirmReset} className="mt-5 space-y-4" data-testid="portal-reset-confirm-form">
                  <div className="space-y-1.5">
                    <Label htmlFor="reset-code">6-digit code</Label>
                    <Input id="reset-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} data-testid="portal-reset-code" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="new-password">New password</Label>
                    <Input id="new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="portal-reset-newpassword" required />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="confirm-password">Confirm new password</Label>
                    <Input id="confirm-password" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} data-testid="portal-reset-confirmpassword" required />
                  </div>

                  {newPassword.length > 0 && (
                    <ul className="space-y-1 text-xs" data-testid="portal-reset-pwrules">
                      {Object.entries(RULE_LABELS).map(([k, label]) => (
                        <li key={k} className={`flex items-center gap-1.5 ${rules[k] ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                          {rules[k] ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{label}
                        </li>
                      ))}
                      <li className={`flex items-center gap-1.5 ${confirmPassword && newPassword === confirmPassword ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                        {confirmPassword && newPassword === confirmPassword ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}Passwords match
                      </li>
                    </ul>
                  )}

                  {error && <p className="text-sm text-red-600 dark:text-red-400" data-testid="portal-reset-confirm-error">{error}</p>}

                  <button type="submit" disabled={busy} data-testid="portal-reset-submit"
                    className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-semibold text-background disabled:opacity-50">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}{busy ? "Changing…" : "Change password"}
                  </button>
                  <div className="flex items-center justify-between">
                    <button type="button" onClick={() => { setCode(""); setNewPassword(""); setConfirmPassword(""); go("login"); }} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                    <button type="button" onClick={resend} disabled={resending} data-testid="portal-reset-resend" className="text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50">
                      {resending ? "Resending…" : "Resend code"}
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
      <PortalFooter />
    </div>
  );
}
